<template>
  <div v-if="showModuloForm === false">
    <MarketingModules></MarketingModules>
  </div>
  <div
    class="row dashboard-page"
    :class="{ 'dashboard-page--dark': darkEnabled }"
    v-else-if="showModuloForm === true"
  >
    <div class="page col-md-12 center">
      <div>
        <Tabs
          :tabs="tabs"
          :color="dashboardAccent"
          :lineColor="dashboardAccent"
          :start="tabStart"
        >
          <template v-slot:dashboard>
            <section class="dashboard-section">
              <h2 class="dashboard-section-title">Visão geral da loja</h2>
              <p class="dashboard-section-help">
                Análise de indicadores sobre pedidos realizados na plataforma
                pelos clientes.
              </p>
              <div class="dashboard-cards row">
                <template v-if="isLoading || cardsLoading">
                  <div
                    class="col-md-4"
                    v-for="n in 3"
                    :key="'card-skeleton-' + n"
                  >
                    <div class="dashboard-card dashboard-card--skeleton">
                      <v-skeleton-loader
                        type="card-heading, list-item-two-line, list-item"
                        class="dashboard-skeleton-card"
                        height="200"
                        :dark="darkEnabled"
                      />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="col-md-4">
                    <div class="dashboard-card dashboard-card--ruptura">
                      <div class="dashboard-card__header">
                        <span class="dashboard-card__label"
                          >Índice de ruptura</span
                        >
                        <v-tooltip bottom max-width="280">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="dashboard-card__help"
                              v-bind="attrs"
                              v-on="on"
                            >
                              far fa-question-circle
                            </v-icon>
                          </template>
                          <span>
                            Percentual de itens não entregues por falta de
                            estoque nos últimos 100 pedidos. Quanto menor,
                            melhor. Abaixo de 8% é bom; acima de 16% requer
                            atenção.
                          </span>
                        </v-tooltip>
                      </div>
                      <div class="dashboard-card__value-wrap">
                        <v-icon class="dashboard-card__icon" color="#00558E">
                          fas fa-box-open
                        </v-icon>
                        <span class="dashboard-card__value">
                          {{ ruptura.valor >= 0 ? ruptura.valor + "%" : "—" }}
                        </span>
                      </div>
                      <p class="dashboard-card__hint">
                        % dos itens com ruptura
                      </p>
                      <FormButton
                        text="Ver detalhes"
                        type="primario"
                        class="dashboard-card__btn"
                        @click="detalhesRuptura"
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="dashboard-card dashboard-card--avaliacao">
                      <div class="dashboard-card__header">
                        <span class="dashboard-card__label"
                          >Avaliação média</span
                        >
                        <v-tooltip bottom max-width="280">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="dashboard-card__help"
                              v-bind="attrs"
                              v-on="on"
                            >
                              far fa-question-circle
                            </v-icon>
                          </template>
                          <span>
                            Média das notas dadas pelos clientes aos pedidos (0
                            a 5). Quanto maior, melhor. Acima de 4,4 é
                            considerado bom.
                          </span>
                        </v-tooltip>
                      </div>
                      <div class="dashboard-card__value-wrap">
                        <v-icon
                          class="dashboard-card__icon"
                          :color="dashboardAccent"
                        >
                          fas fa-star
                        </v-icon>
                        <span class="dashboard-card__value">
                          {{
                            avaliacaoMedia.valor != null
                              ? avaliacaoMedia.valor
                              : "—"
                          }}
                        </span>
                        <span class="dashboard-card__unit">/ 5</span>
                      </div>
                      <p class="dashboard-card__hint">
                        Nota média dos clientes
                      </p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="dashboard-card dashboard-card--tempo">
                      <div class="dashboard-card__header">
                        <span class="dashboard-card__label"
                          >Tempo médio de atendimento</span
                        >
                        <v-tooltip bottom max-width="280">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="dashboard-card__help"
                              v-bind="attrs"
                              v-on="on"
                            >
                              far fa-question-circle
                            </v-icon>
                          </template>
                          <span>
                            Tempo médio entre o pedido e a entrega. Quanto
                            menor, melhor. Até 2h é ótimo; entre 2h e 4h é
                            aceitável; acima de 4h merece atenção.
                          </span>
                        </v-tooltip>
                      </div>
                      <div class="dashboard-card__value-wrap">
                        <v-icon
                          class="dashboard-card__icon"
                          :color="dashboardAccent"
                        >
                          fas fa-clock
                        </v-icon>
                        <span class="dashboard-card__value">
                          {{ formatTempoAtendimentoExibicao() }}
                        </span>
                      </div>
                      <p class="dashboard-card__hint">
                        Pedido até entrega
                      </p>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <section class="dashboard-section">
              <h2 class="dashboard-section-title">Vendas e pedidos</h2>
              <p class="dashboard-section-help">
                Evolução do ticket médio e da quantidade de pedidos por dia.
                Valores de cancelamento ajudam a avaliar a saúde das vendas.
              </p>
              <div class="row">
                <template v-if="isLoading || loading.ticket_medio">
                  <div
                    class="col-md-12"
                    v-for="n in 2"
                    :key="'chart-skeleton-' + n"
                  >
                    <div class="dashboard-chart-skeleton">
                      <v-skeleton-loader
                        type="image"
                        class="dashboard-skeleton-chart"
                        height="350"
                        :dark="darkEnabled"
                      />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="col-md-12">
                    <div class="dashboard-chart-header">
                      <span>Ticket médio</span>
                      <v-tooltip bottom max-width="280">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small v-bind="attrs" v-on="on">
                            far fa-question-circle
                          </v-icon>
                        </template>
                        <span>
                          Valor médio dos pedidos (R$) e dos cancelamentos por
                          dia. Compare as linhas para ver a relação entre vendas
                          realizadas e canceladas.
                        </span>
                      </v-tooltip>
                    </div>
                    <MultiBar
                      :name="'ticket_medio'"
                      :label="''"
                      :x-axis-label="ticket.xAxisLabel"
                      :y-axis-label="ticket.yAxisLabel"
                      :datum="ticket.datum"
                      :dark="darkEnabled"
                    />
                  </div>
                  <div class="col-md-12">
                    <div class="dashboard-chart-header">
                      <span>Total de pedidos</span>
                      <v-tooltip bottom max-width="280">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small v-bind="attrs" v-on="on">
                            far fa-question-circle
                          </v-icon>
                        </template>
                        <span>
                          Quantidade de pedidos entregues e de pedidos
                          cancelados por dia. Acompanhe a tendência ao longo do
                          tempo.
                        </span>
                      </v-tooltip>
                    </div>
                    <MultiBar
                      :name="'total_pedidos'"
                      :label="''"
                      :x-axis-label="totalPedidos.xAxisLabel"
                      :y-axis-label="totalPedidos.yAxisLabel"
                      :datum="totalPedidos.datum"
                      :dark="darkEnabled"
                    />
                  </div>
                </template>
              </div>
            </section>
          </template>
          <template v-slot:estatisticas class="">
            <v-row
              v-if="!loading.kpi"
              :kpis="kpis"
              class="pl-1 dashboard-page-content"
              no-gutters
            >
              <v-col class="px-1">
                <FormDatePicker
                  v-model="filter.initDate"
                  label="Data inicial"
                  :min="filter.startingDate"
                  :max="filter.finalDate"
                />
              </v-col>
              <v-col class="px-1">
                <FormDatePicker
                  v-model="filter.finalDate"
                  :min="filter.startingDate"
                  label="Data final"
                />
              </v-col>
              <v-col cols="auto" class="px-1">
                <FormButton
                  text="Buscar"
                  type="primario"
                  @click="filter.search = true"
                />
              </v-col>
              <v-col cols="auto" class="pt-0 px-1">
                <v-tooltip bottom max-width="280">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="dashboard-card__help"
                      v-bind="attrs"
                      v-on="on"
                    >
                      far fa-question-circle
                    </v-icon>
                  </template>
                  <span>
                    O filtro de datas aplica-se apenas aos gráficos que não
                    estão fixos nos últimos 90 dias.
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
            <h3 class="dashboard-section-title">KPI's</h3>
            <p class="dashboard-section-help mb-0">
              Análise de indicadores sobre a loja.
            </p>
            <KpisFornecedor
              v-if="!loading.kpi"
              :kpis="kpis"
              :filter="filter"
              @change-search="val => (filter.search = val)"
            />
          </template>
        </Tabs>
      </div>
    </div>

    <!-- Modal: Detalhes da ruptura -->
    <v-dialog
      v-model="show.detalhesRuptura"
      max-width="900px"
      scrollable
      persistent
      :dark="darkEnabled"
      content-class="dashboard-ruptura-modal"
    >
      <v-card max-height="85vh" class="d-flex flex-column">
        <v-card-title
          class="dashboard-modal-title font-weight-bold"
          :style="{ color: dashboardAccent }"
        >
          Produtos com ruptura
          <v-spacer />
          <v-btn
            icon
            small
            @click="show.detalhesRuptura = false"
            aria-label="Fechar"
            :color="dashboardAccent"
          >
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="flex-grow-1 overflow-auto pa-4">
          <div v-if="ruptura.detalhes && ruptura.detalhes.produtos">
            <DataTable
              classe="elevation-0"
              :headers="headers_produtos"
              :data="ruptura.detalhes.produtos"
              :itemsPerPage="30"
              sortBy="produto"
              :sortDesc="false"
              ref="datatable_produtos"
              :auto-search="false"
            >
              <template v-slot:idpedido="{ col }">
                {{ col.nome }}
              </template>
              <template v-slot:datapedido="{ col }">
                {{ col.ruptura_financeira_produto.toFixed(2) }}
              </template>
            </DataTable>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 mt-4">
          <p
            class="dashboard-modal-desc"
            :style="{ color: modalSecondaryText }"
          >
            * Produtos com ruptura nos últimos 100 pedidos (valor financeiro da
            ruptura).
          </p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { DIAGNOSTICO_LOAD, KPI_LOAD } from "@/store/actions/diagnostico";
import FormDatePicker from "@/components/form/FormDatePicker";
import FormButton from "@/components/form/FormButton";
import Tabs from "@/components/Tabs";
import KpisFornecedor from "@/components/KpisFornecedor";
import { mapGetters } from "vuex";
import * as _ from "lodash";
import * as moment from "moment";
import MarketingModules from "@/components/MarketingModules";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import {
  AVALIACAO_LOAD,
  RUPTURA_DETALHE,
  RUPTURA_LOAD,
  TEMPO_MEDIO_LOAD,
  TICKET_MEDIO_LOAD
} from "../store/actions/diagnostico";
import DataTable from "@/components/DataTable";
import MultiBar from "../components/charts/MultiBar.vue";

export default {
  name: "Dashboard",
  components: {
    FormDatePicker,
    KpisFornecedor,
    FormButton,
    Tabs,
    MarketingModules,
    DataTable,
    MultiBar
  },
  data: () => ({
    menu: {},
    diagnoses: [],
    kpis: [],
    totalConclusao: 0,
    loading: {
      kpi: true,
      diagnoses: true,
      ruptura: true,
      avaliacao_media: true,
      tempo_medio: true,
      ticket_medio: true
    },
    active: null,
    filter: {
      startingDate: moment()
        .add(-90, "days")
        .format("YYYY-MM-DD"),
      initDate: moment()
        .add(-90, "days")
        .format("YYYY-MM-DD"),
      finalDate: moment().format("YYYY-MM-DD"),
      formattedInitDate: moment()
        .add(-90, "days")
        .format("DD/MM/YYYY"),
      formattedFinalDate: moment().format("DD/MM/YYYY"),
      // nowDate: new Date().toISOString().substr(0, 10),
      search: false
    },
    tabs: [
      {
        key: "dashboard",
        text: "Dashboard"
      },
      {
        key: "estatisticas",
        text: "Estatísticas"
      }
    ],
    tabStart: 0,
    modulosForm: [ModulesEnum.TODOS],
    ruptura: {
      valor: -1
    },
    avaliacaoMedia: {
      valor: 0
    },
    tempoAtendimentoMedio: {
      valor: 0
    },
    ticket: {
      xAxisLabel: "Data",
      yAxisLabel: "Ticket médio",
      datum: []
    },
    totalPedidos: {
      xAxisLabel: "Data",
      yAxisLabel: "Total de pedidos",
      datum: []
    },
    show: {
      detalhesRuptura: false
    },
    headers_produtos: [
      { text: "Produto", value: "nome" },
      { text: "Ruptura (R$)", value: "ruptura_financeira_produto" }
    ]
  }),
  created() {
    this.getKpis();
    this.getDignoses();
    this.getIndiceRuptura();
    this.getAvaliacaoMedia();
    this.getTempoMedioAtendimento();
    this.getTicketMedio();
  },
  computed: {
    ...mapGetters([
      "getEmpresaId",
      "getFornecedorId",
      "getFornecedorTag",
      "moduloFornecedorAtivo",
      "darkEnabled"
    ]),
    dashboardAccent() {
      return this.darkEnabled ? "#64b5f6" : "#00558E";
    },
    modalSecondaryText() {
      return this.darkEnabled ? "rgba(255, 255, 255, 0.7)" : "#555";
    },
    endpoint() {
      return `/api/v4/report/datatable/${this.getFornecedorId}`;
    },
    isLoading() {
      return this.loading.kpi && this.loading.diagnoses;
    },
    showModuloForm() {
      return this.moduloFornecedorAtivo(this.modulosForm);
    },
    cardsLoading() {
      return (
        this.loading.ruptura ||
        this.loading.avaliacao_media ||
        this.loading.tempo_medio
      );
    }
  },
  watch: {
    "filter.initDate"(val) {
      this.filter.formattedInitDate = moment(val).format("DD/MM/YYYY");
    },
    "filter.finalDate"(val) {
      this.filter.formattedFinalDate = moment(val).format("DD/MM/YYYY");
    }
  },
  methods: {
    selectTab(val) {
      this.tabStart = val;
    },
    getDignoses() {
      this.$store
        .dispatch(DIAGNOSTICO_LOAD)
        .then(result => {
          if (result) {
            this.diagnoses = result.data;
            this.totalConclusao = result.totalConclusao;
          }
          this.loading.diagnoses = false;
        })
        .catch(() => {
          this.loading.diagnoses = false;
        });
    },
    getKpis() {
      this.$store
        .dispatch(KPI_LOAD)
        .then(result => {
          if (result) {
            this.kpis = result || [];
          }
          this.loading.kpi = false;
        })
        .catch(() => {
          this.loading.kpi = false;
        });
    },
    scrollTo(id) {
      const el = this.$refs[id];
      const element = _.get(el, "0.$el");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    formatTempoAtendimentoExibicao() {
      const t = this.tempoAtendimentoMedio;
      if (!t) return "—";
      if (t.texto) {
        const parts = String(t.texto)
          .trim()
          .split(/\s+/);
        const dias = parseInt(parts[0], 10) || 0;
        const horas = parseInt(parts[1], 10) || 0;
        const minutos = parseInt(parts[2], 10) || 0;
        const segundos = [];
        if (dias > 0) {
          segundos.push(dias === 1 ? "1 dia" : `${dias} dias`);
        }
        if (horas > 0) {
          segundos.push(horas === 1 ? "1 hora" : `${horas} horas`);
        }
        if (minutos > 0 || segundos.length === 0) {
          const m =
            minutos === 1
              ? "1 minuto"
              : minutos === 0
              ? "0 minutos"
              : `${minutos} minutos`;
          segundos.push(m);
        }
        return segundos.length > 1
          ? segundos.slice(0, -1).join(" ") +
              " e " +
              segundos[segundos.length - 1]
          : segundos[0];
      }
      if (t.valor != null && t.valor !== "") {
        return Number(t.valor) + " h";
      }
      return "—";
    },
    getAvaliacaoMedia() {
      this.$store
        .dispatch(AVALIACAO_LOAD)
        .then(result => {
          if (result) {
            this.avaliacaoMedia.valor = result.notaavaliacao;
          }
          this.loading.avaliacao_media = false;
        })
        .catch(() => {
          this.loading.avaliacao_media = false;
        });
    },
    getIndiceRuptura() {
      this.$store
        .dispatch(RUPTURA_LOAD)
        .then(result => {
          if (result) {
            if (result.total_ruptura > 0 && result.total_vendido > 0) {
              this.ruptura.valor = Number(
                (
                  Number(result.total_ruptura / result.total_vendido) * 100
                ).toFixed(2)
              );
            } else {
              this.ruptura.valor = 0;
            }
          }
          this.loading.ruptura = false;
        })
        .catch(() => {
          this.loading.ruptura = false;
        });
    },
    getTempoMedioAtendimento() {
      this.$store
        .dispatch(TEMPO_MEDIO_LOAD)
        .then(result => {
          if (result) {
            this.tempoAtendimentoMedio.valor =
              result.tempo_medio_atendimento_horas;
            this.tempoAtendimentoMedio.texto =
              result.tempo_medio_atendimento_text;
          }
          this.loading.tempo_medio = false;
        })
        .catch(() => {
          this.loading.tempo_medio = false;
        });
    },
    getTicketMedio() {
      this.$store
        .dispatch(TICKET_MEDIO_LOAD)
        .then(result => {
          if (result) {
            var datumValuesTicket = [];
            var datumValuesCancel = [];
            var datumValuesNumPedidos = [];
            var datumValCountCancels = [];

            result.map(item => {
              datumValuesTicket.push(
                this.datumItem(Number(item.ticket_medio), item.dia)
              );
              datumValuesNumPedidos.push(
                this.datumItem(Number(item.count_pedidos), item.dia)
              );
              datumValuesCancel.push(
                this.datumItem(Number(item.valor_cancelado_medio), item.dia)
              );
              datumValCountCancels.push(
                this.datumItem(Number(item.count_pedidos_cancelados), item.dia)
              );
            });

            this.ticket.datum = [
              {
                values: this.dedupeDatumValues(datumValuesTicket),
                key: "Ticket médio (R$)",
                color: "#50B748"
              },
              {
                values: this.dedupeDatumValues(datumValuesCancel),
                key: "Cancel. médio (R$)",
                color: "#00558E"
              }
            ];

            this.totalPedidos.datum = [
              {
                values: this.dedupeDatumValues(datumValuesNumPedidos),
                key: "Num. pedidos",
                color: "#50B748"
              },
              {
                values: this.dedupeDatumValues(datumValCountCancels),
                key: "Num. cancelamentos",
                color: "#00558E"
              }
            ];
          }
          this.loading.ticket_medio = false;
        })
        .catch(() => {
          this.loading.ticket_medio = false;
        });
    },
    detalhesRuptura() {
      this.$store
        .dispatch(RUPTURA_DETALHE)
        .then(result => {
          if (result) {
            this.ruptura.detalhes = {
              produtos: result.produtos_mais_rompidos
            };
          }
          this.show.detalhesRuptura = true;
        })
        .catch(() => {
          this.show.detalhesRuptura = false;
        });
    },
    datumItem(y, label) {
      return {
        x: this.normalizeDatumLabel(label),
        y: y
      };
    },
    /**
     * Se o valor for uma data (ex: DD/MM/YY ou DD/MM/YYYY), concatena " 12:00:00".
     */
    normalizeDatumLabel(label) {
      if (label == null) return label;
      const s = String(label).trim();
      if (!s) return s;
      if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s) && !s.includes(" ")) {
        return s + " 12:00:00";
      }
      return s;
    },
    /**
     * Remove duplicidades por x: mantém apenas a primeira ocorrência de cada x.
     * @param {Array<{x,y}>} values
     */
    dedupeDatumValues(values) {
      if (!values || !values.length) return values;
      const seen = new Set();
      return values.filter(({ x }) => {
        if (seen.has(x)) return false;
        seen.add(x);
        return true;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.dashboard-page {
  --dash-accent: #00558e;
  --dash-green: #50b748;
  --dash-card-bg: #fff;
  --dash-card-border: #e0e0e0;
  --dash-skeleton-border: #e0e0e0;
  --dash-title: #00558e;
  --dash-help: #555;
  --dash-label: #333;
  --dash-value: #00558e;
  --dash-muted: #666;
  --dash-chart-border: #eee;
  --dash-hover-shadow: rgba(0, 85, 142, 0.12);

  &.dashboard-page--dark {
    --dash-accent: #64b5f6;
    --dash-green: #81c784;
    --dash-card-bg: #1d1d1d;
    --dash-card-border: rgba(255, 255, 255, 0.12);
    --dash-skeleton-border: rgba(255, 255, 255, 0.12);
    --dash-title: #90caf9;
    --dash-help: rgba(255, 255, 255, 0.65);
    --dash-label: rgba(255, 255, 255, 0.87);
    --dash-value: #90caf9;
    --dash-muted: rgba(255, 255, 255, 0.6);
    --dash-chart-border: rgba(255, 255, 255, 0.1);
    --dash-hover-shadow: rgba(0, 0, 0, 0.4);
  }
}

.dashboard-page-content {
  background: var(--dash-card-bg);
}

.dashboard-section {
  padding-left: 0.75rem;
  padding-bottom: 2rem;
  background: var(--dash-card-bg);
}

.dashboard-section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dash-title);
  background: var(--dash-card-bg);
  padding-bottom: 0.35rem;
}

.dashboard-section-help {
  font-size: 0.875rem;
  color: var(--dash-help);
  background: var(--dash-card-bg);
  padding-bottom: 1.25rem;
}

.dashboard-cards {
  display: flex;
  flex-wrap: wrap;
  padding: 0 -0.5rem;
  background: var(--dash-card-bg);
}

.dashboard-card {
  background: var(--dash-card-bg);
  border: 1px solid var(--dash-card-border);
  border-radius: 8px;
  padding: 1.25rem;
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease;
  border-left: 4px solid var(--dash-accent);
}

.dashboard-card:hover {
  box-shadow: 0 2px 8px var(--dash-hover-shadow);
}

.dashboard-card--skeleton {
  border-left-color: var(--dash-skeleton-border);
  overflow: hidden;
}

.dashboard-skeleton-card {
  padding: -1.25rem;
}

.dashboard-chart-skeleton {
  min-height: 350px;
  padding-bottom: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--dash-chart-border);
}

.dashboard-skeleton-chart {
  width: 100%;
}

.dashboard-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.75rem;
}

.dashboard-card__label {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--dash-label);
}

.dashboard-card__help {
  color: var(--dash-accent);
  cursor: help;
  opacity: 0.8;
}

.dashboard-card__value-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.25rem;
}

.dashboard-card__icon {
  font-size: 1.5rem !important;
  opacity: 0.9;
}

.dashboard-card__value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--dash-value);
}

.dashboard-card__unit {
  font-size: 1.5rem;
  color: var(--dash-muted);
  font-weight: 500;
}

.dashboard-card__hint {
  font-size: 0.9rem;
  color: var(--dash-muted);
  padding-bottom: 1rem;
  flex: 1;
}

.dashboard-card__btn {
  margin-top: auto;
}

.dashboard-chart-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  font-weight: 600;
  color: var(--dash-title);
}

.dashboard-chart-header .v-icon {
  color: var(--dash-accent);
  cursor: help;
  opacity: 0.8;
}

.row {
  width: 100%;
}

.dashboard-modal-title {
  color: var(--dash-accent);
  font-weight: 600;
}

.dashboard-modal-desc {
  color: var(--dash-help);
  font-size: 0.95rem;
}
</style>
